/**
 * Strip credentials out of a URL before it is handed to error telemetry.
 *
 * Nine public routes carry a bearer-equivalent token as a **path segment** and
 * three more carry one as a **query parameter**, and every client-side error
 * reporter was posting `window.location.href` verbatim (backlog R7). Four of
 * those tokens are, by deliberate design, *durable capability URLs* stored raw
 * so one stable link keeps working for the life of the document
 * (`invoices.payment_token`, `offers.accept_token`,
 * `questionnaire_responses.public_token`, `contracts.view_token` — see
 * CLAUDE.md). That design makes this leak worse, not better: the token in the
 * URL does not expire, so a single copy in an error report is a live,
 * unauthenticated pay / accept / view credential for the rest of the
 * document's life.
 *
 * The redaction is **whitelist-shaped on purpose**. It matches the page against
 * the router's own route table and puts the *pattern* back
 * (`/invoices/pay/:token`), rather than trying to recognise a token by its
 * shape — a regex that tries to spot "something token-looking" is exactly the
 * thing that misses one. `looksLikeSecret` below is a second net for a route
 * nobody remembered to add here, never the first line of defence.
 */

/**
 * Every route whose dynamic segment is a credential, written as the router
 * writes it. Keep this in step with `src/router/index.tsx`; the regression
 * lock in `__tests__/redactUrl.test.ts` enumerates them.
 *
 * Deliberately NOT here: `/book/:slug` (the studio's own public booking slug —
 * printed on cards and embedded in iframes, so it is neither secret nor
 * sensitive, and knowing which studio errored is useful).
 */
export const SENSITIVE_ROUTE_PATTERNS: readonly string[] = [
  // Account-binding tokens — hash-only in the DB, one-shot.
  '/invite/:token',
  '/reset-password/:token',
  '/confirm-email-change/:token',
  '/confirm-leave-studio/:token',
  '/confirm-role/:token',
  '/admin/confirm-deletion/:token',
  // Durable capability URLs — stored raw, never expire, worst case if leaked.
  '/q/:token',
  '/contracts/accept/:token',
  '/offers/accept/:token',
  '/invoices/pay/:token',
  // Unlisted share links. The gallery slug is the only thing standing between
  // a stranger and a client's photographs, and it carries the shoot's title.
  '/g/:slug',
  // A download page: the export key is the capability to fetch every ZIP part of a gallery.
  '/download/:exportKey',
  // The API call behind that page (not a router route — it reaches error text through axios
  // messages and stack frames, where the free-text scrubber matches it by prefix).
  '/public/archive-exports/:exportKey',
  // A short link resolves to one of the above, so it is worth exactly as much.
  '/r/:code',
]

/** What replaces a redacted query value or an unrecognised secret-shaped segment. */
export const REDACTED = '<redacted>'

interface CompiledRoute {
  /** Literal path segments, with `null` standing in for the dynamic one. */
  segments: (string | null)[]
  /** The placeholder to put back, e.g. `:token`. */
  placeholder: string
  /** Literal prefix, for scrubbing a URL embedded in free text. */
  prefix: string
}

const COMPILED: CompiledRoute[] = SENSITIVE_ROUTE_PATTERNS.map(pattern => {
  const parts = pattern.split('/').filter(Boolean)
  const dynamicIndex = parts.findIndex(p => p.startsWith(':'))
  return {
    segments: parts.map(p => (p.startsWith(':') ? null : p)),
    placeholder: parts[dynamicIndex],
    prefix: '/' + parts.slice(0, dynamicIndex).join('/'),
  }
})

/**
 * Second net, never the first: a path segment that is long enough and mixed-case
 * enough to be a base64url secret. 128 bits of base64url is 22 characters and
 * essentially always mixes case; the app's own slugs are lowercase-and-hyphens,
 * so requiring both cases keeps `/guide/setting-up-your-studio-profile`
 * readable while still catching a token route that nobody added above.
 */
function looksLikeSecret(segment: string): boolean {
  if (segment.length < 22) return false
  if (!/^[A-Za-z0-9_-]+$/.test(segment)) return false
  return /[a-z]/.test(segment) && /[A-Z]/.test(segment)
}

function redactPathname(pathname: string): string {
  const parts = pathname.split('/')
  // parts[0] is the empty string before the leading slash.
  const segments = parts.slice(1)

  for (const route of COMPILED) {
    if (segments.length !== route.segments.length) continue
    const matches = route.segments.every(
      (literal, i) => literal === null || literal === decodeURIComponent(segments[i]),
    )
    if (matches) {
      return '/' + route.segments.map(literal => literal ?? route.placeholder).join('/')
    }
  }

  return '/' + segments.map(s => (looksLikeSecret(s) ? REDACTED : s)).join('/')
}

/**
 * Redact a page URL for telemetry.
 *
 * - Path: the matching route pattern is put back in place of the credential.
 * - Query: **every** value is replaced, keys kept. Not a blacklist of parameter
 *   names — `?token=`, `?code=`, `?state=` and `?memberId=` are the ones in the
 *   router today, and the next one to be added would not be on any list we
 *   wrote. Keys alone are enough to debug from, and CLAUDE.md's rule is that
 *   personal or sensitive data never belongs in a query string anyway.
 * - Hash: kept when it is a plain fragment identifier, dropped the moment it
 *   looks like it is carrying a payload (`=` or `&`).
 *
 * Accepts absolute and root-relative URLs; anything unparseable comes back as
 * the literal `<redacted>` rather than being passed through.
 */
export function redactUrl(href: string): string {
  if (!href) return href

  let parsed: URL
  let wasRelative = false
  try {
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href)) {
      parsed = new URL(href)
    } else {
      wasRelative = true
      parsed = new URL(href, 'http://redacted.invalid')
    }
  } catch {
    return REDACTED
  }

  const path = redactPathname(parsed.pathname)

  let query = ''
  if (parsed.search) {
    const keys = Array.from(parsed.searchParams.keys())
    query = '?' + keys.map(k => `${k}=${REDACTED}`).join('&')
  }

  let hash = ''
  if (parsed.hash && !/[=&]/.test(parsed.hash)) {
    hash = parsed.hash
  } else if (parsed.hash) {
    hash = `#${REDACTED}`
  }

  const origin = wasRelative ? '' : parsed.origin
  return `${origin}${path}${query}${hash}`
}

/**
 * Scrub a sensitive URL that is embedded in free text — an error message such as
 * react-router's `No route matches URL "/invoices/pay/<token>"`, or a stack
 * frame. Only the known route shapes are rewritten; the rest of the string is
 * left exactly as it was, because a mangled stack trace is a useless one.
 */
export function redactSensitiveText(text: string): string
export function redactSensitiveText(text: string | undefined): string | undefined
export function redactSensitiveText(text: string | undefined): string | undefined {
  if (!text) return text
  let out = text
  for (const route of COMPILED) {
    // Every credential in the table is base64url (`A-Za-z0-9_-`), a lowercase
    // slug, or an alphanumeric short code, so match exactly that plus the
    // percent-escapes a URL may carry. Matching "everything up to whitespace"
    // instead would swallow the `:12:34)` of the stack frame the token sits in.
    const re = new RegExp(`(${escapeRegExp(route.prefix)}/)[A-Za-z0-9._~%-]+`, 'g')
    out = out.replace(re, `$1${route.placeholder}`)
  }
  return out
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * The shape of a Sentry event, narrowed to the two places a page URL reaches it.
 * Typed structurally so this module never imports `@sentry/react` — that SDK is
 * loaded lazily and only when a DSN is configured (see main.tsx).
 */
export interface UrlBearingEvent {
  request?: { url?: string; headers?: Record<string, string> }
  breadcrumbs?: { data?: Record<string, unknown> }[]
}

/**
 * Sentry fills `request.url` from `window.location.href` on its own, so the
 * capability token reaches a third party without any of our code passing it.
 * Wire this in as `beforeSend`.
 */
export function redactTelemetryEvent<T extends UrlBearingEvent>(event: T): T {
  if (event.request?.url) {
    event.request.url = redactUrl(event.request.url)
  }
  const referer = event.request?.headers?.Referer ?? event.request?.headers?.referer
  if (event.request?.headers && referer) {
    const key = 'Referer' in event.request.headers ? 'Referer' : 'referer'
    event.request.headers[key] = redactUrl(referer)
  }
  for (const crumb of event.breadcrumbs ?? []) {
    const from = crumb.data?.from
    const to = crumb.data?.to
    if (typeof from === 'string') crumb.data!.from = redactUrl(from)
    if (typeof to === 'string') crumb.data!.to = redactUrl(to)
  }
  return event
}
